import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Pencil } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface TokenInfo {
  valid: boolean;
  partnerName: string;
  partnerLogo?: string | null;
  platformName?: string | null;
  clientName?: string | null;
  clientEmail?: string | null;
  deliveryModel: string;
}

const TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Australia/Sydney',
  'Pacific/Auckland',
];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  fontSize: 14,
  fontFamily: 'var(--font-body)',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: 4,
};

export default function ClientOnboardingWizardPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [step, setStep] = useState(0); // 0 = loading/validating
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [error, setError] = useState('');

  // Step 1
  const [orgName, setOrgName] = useState('');

  // Step 2
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Step 3
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [githubOrg, setGithubOrg] = useState('');

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [activating, setActivating] = useState(false);

  // Sidebar logo error fallback
  const [logoError, setLogoError] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setError('No invitation token provided.');
      return;
    }

    fetch(`${API}/partner/clients/invite/${token}/validate`)
      .then(async r => {
        if (r.status === 404) throw new Error('Invitation not found.');
        if (r.status === 410) throw new Error('This invitation has expired or is no longer valid.');
        if (!r.ok) throw new Error('Failed to validate invitation.');
        return r.json();
      })
      .then((info: TokenInfo) => {
        setTokenInfo(info);
        if (info.clientName) setOrgName(info.clientName);
        if (info.clientEmail) setAdminEmail(info.clientEmail);
        setStep(1);
      })
      .catch(e => setError(e.message));
  }, [token]);

  const handleComplete = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/partner/clients/onboarding/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          orgName,
          adminName,
          adminEmail,
          adminPassword: password,
          timezone,
          githubOrg: githubOrg || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? `Onboarding failed (${res.status})`);
      }

      const data = await res.json();
      if (data.token) {
        localStorage.setItem('blueprint_token', data.token);
        if (data.user) {
          localStorage.setItem('blueprint_user', JSON.stringify(data.user));
        }
        setActivating(true);
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        navigate('/login');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Onboarding failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const passwordStrength = (() => {
    if (!password) return { label: '', color: '' };
    if (password.length < 8) return { label: 'Too short', color: 'var(--error)' };
    const hasUpper = /[A-Z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const score = [hasUpper, hasDigit, hasSpecial, password.length >= 12].filter(Boolean).length;
    if (score <= 1) return { label: 'Weak', color: 'var(--error)' };
    if (score === 2) return { label: 'Fair', color: 'var(--warning)' };
    return { label: 'Strong', color: 'var(--success)' };
  })();

  // Error screen
  if (error && !tokenInfo) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center', maxWidth: 420, padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#9888;</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            Invalid Invitation
          </h1>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 24 }}>
            {error}
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
            Please contact your service provider for a new invitation link.
          </p>
        </div>
      </div>
    );
  }

  // Activating overlay — shown after successful onboarding before dashboard redirect
  if (activating) {
    return (
      <div
        data-testid="activating-overlay"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg)',
          gap: 20,
        }}
      >
        {tokenInfo?.partnerLogo && !logoError ? (
          <img
            src={tokenInfo.partnerLogo.startsWith('/') ? `${API}${tokenInfo.partnerLogo}` : tokenInfo.partnerLogo}
            alt={tokenInfo?.platformName ?? 'Partner'}
            onError={() => setLogoError(true)}
            style={{ maxWidth: 180, maxHeight: 48, marginBottom: 8 }}
          />
        ) : (
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
            {tokenInfo?.platformName ?? 'BluePrint'}
          </span>
        )}
        <div
          style={{
            width: 32,
            height: 32,
            border: '3px solid var(--border)',
            borderTopColor: 'var(--primary)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>
          Setting up your workspace...
        </p>
      </div>
    );
  }

  // Loading
  if (step === 0) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>Validating invitation...</p>
      </div>
    );
  }

  const platformName = tokenInfo?.platformName ?? 'BluePrint';
  const partnerName = tokenInfo?.partnerName ?? 'Partner';

  const steps = [
    { num: 1, label: 'Welcome' },
    { num: 2, label: 'Admin Account' },
    { num: 3, label: 'Configuration' },
    { num: 4, label: 'Review & Activate' },
  ];

  const canNext =
    step === 1 ? orgName.trim().length > 0 :
    step === 2 ? adminName.trim().length > 0 && adminEmail.trim().length > 0 && password.length >= 8 && password === confirmPassword :
    step === 3 ? true :
    true;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--bg)' }}>
      {/* Sidebar */}
      <div style={{ width: 260, background: 'var(--surface)', borderRight: '1px solid var(--border)', padding: '32px 20px', flexShrink: 0 }}>
        {tokenInfo?.partnerLogo && !logoError ? (
          <div style={{ marginBottom: 24 }}>
            <img
              src={tokenInfo.partnerLogo.startsWith('/') ? `${API}${tokenInfo.partnerLogo}` : tokenInfo.partnerLogo}
              alt={partnerName}
              onError={() => setLogoError(true)}
              style={{ maxWidth: 160, maxHeight: 40 }}
            />
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
              {platformName}
            </div>
          </div>
        ) : (
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 24 }}>
            {platformName}
          </h2>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {steps.map(s => {
            const isComplete = s.num < step;
            const isCurrent = s.num === step;
            return (
              <div key={s.num} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-display)',
                  background: isComplete ? 'var(--success)' : isCurrent ? 'var(--primary)' : 'var(--surface-alt)',
                  color: isComplete || isCurrent ? '#fff' : 'var(--text-secondary)',
                  border: isCurrent ? 'none' : '1px solid var(--border)',
                }}>
                  {isComplete ? '\u2713' : s.num}
                </div>
                <span style={{
                  fontFamily: 'var(--font-body)', fontSize: 13,
                  fontWeight: isCurrent ? 600 : 400,
                  color: isCurrent ? 'var(--text)' : 'var(--text-secondary)',
                }}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: '40px 48px', maxWidth: 640 }}>
        {error && (
          <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 20, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)' }}>
            {error}
          </div>
        )}

        {/* Step 1 — Welcome */}
        {step === 1 && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              Welcome to {platformName}
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 32 }}>
              You&apos;ve been invited by <strong>{partnerName}</strong> to set up your project delivery workspace.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Organisation Name *</label>
              <input
                type="text"
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                placeholder="Your company name"
                style={inputStyle}
              />
            </div>
            <div style={{ padding: '12px 16px', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 'var(--radius-sm)' }}>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                <strong>Self-Service</strong> — your team manages projects directly with {platformName}&apos;s AI agents.
              </p>
            </div>
          </>
        )}

        {/* Step 2 — Admin Account */}
        {step === 2 && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              Create Admin Account
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 32 }}>
              This account will be the workspace administrator for {orgName || 'your organisation'}.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Full Name *</label>
                <input type="text" value={adminName} onChange={e => setAdminName(e.target.value)} placeholder="Jane Smith" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Email *</label>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={e => setAdminEmail(e.target.value)}
                  placeholder="jane@company.com"
                  style={inputStyle}
                  readOnly={!!tokenInfo?.clientEmail}
                />
              </div>
              <div>
                <label style={labelStyle}>Password * (min 8 characters)</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Create a strong password"
                    style={{ ...inputStyle, paddingRight: 40 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    style={{
                      position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                      background: 'transparent', border: 'none', cursor: 'pointer', padding: 6,
                      display: 'flex', alignItems: 'center', color: 'var(--text-secondary)',
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {password && (
                  <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ height: 4, flex: 1, borderRadius: 2, background: 'var(--border)' }}>
                      <div style={{ height: '100%', borderRadius: 2, background: passwordStrength.color, width: passwordStrength.label === 'Strong' ? '100%' : passwordStrength.label === 'Fair' ? '60%' : '30%', transition: 'width 200ms' }} />
                    </div>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: passwordStrength.color }}>{passwordStrength.label}</span>
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Confirm Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    style={{ ...inputStyle, paddingRight: 40 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(s => !s)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    style={{
                      position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                      background: 'transparent', border: 'none', cursor: 'pointer', padding: 6,
                      display: 'flex', alignItems: 'center', color: 'var(--text-secondary)',
                    }}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {confirmPassword && confirmPassword !== password && (
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--error)', marginTop: 2 }}>Passwords do not match</span>
                )}
              </div>
            </div>
          </>
        )}

        {/* Step 3 — Configuration */}
        {step === 3 && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              Configuration
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 32 }}>
              These can be changed later in Settings.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Timezone</label>
                <select value={timezone} onChange={e => setTimezone(e.target.value)} style={inputStyle}>
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>GitHub Organisation (optional)</label>
                <input type="text" value={githubOrg} onChange={e => setGithubOrg(e.target.value)} placeholder="your-org" style={inputStyle} />
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, display: 'block' }}>
                  Can be configured later in Settings &gt; BYOI.
                </span>
              </div>
            </div>
          </>
        )}

        {/* Step 4 — Review */}
        {step === 4 && (
          <>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              Review & Activate
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 24 }}>
              Confirm your workspace details before activation.
            </p>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {([
                { label: 'Organisation Name', value: orgName, editStep: 1 },
                { label: 'Full Name', value: adminName, editStep: 2 },
                { label: 'Email', value: adminEmail, editStep: 2 },
                { label: 'Timezone', value: timezone, editStep: 3 },
                { label: 'GitHub Org', value: githubOrg || '(not set)', editStep: 3 },
                { label: 'Invited by', value: partnerName, editStep: null },
              ] as const).map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>{item.label}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{item.value}</span>
                    {item.editStep !== null && (
                      <button
                        type="button"
                        onClick={() => setStep(item.editStep)}
                        aria-label={`Edit ${item.label}`}
                        title={`Edit ${item.label}`}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: 2,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: 'var(--text-secondary)',
                          opacity: 0.5,
                          transition: 'opacity 150ms, color 150ms',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.opacity = '1';
                          e.currentTarget.style.color = 'var(--text)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.opacity = '0.5';
                          e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                      >
                        <Pencil size={13} />
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
          {step > 1 ? (
            <button
              onClick={() => setStep(step - 1)}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                padding: '10px 20px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', background: 'transparent',
                color: 'var(--text)', cursor: 'pointer',
              }}
            >
              Back
            </button>
          ) : <div />}

          {step < 4 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canNext}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                padding: '10px 24px', borderRadius: 'var(--radius-sm)',
                border: 'none', background: 'var(--primary)', color: '#fff',
                cursor: canNext ? 'pointer' : 'not-allowed',
                opacity: canNext ? 1 : 0.5,
              }}
            >
              Continue
            </button>
          ) : (
            <button
              onClick={handleComplete}
              disabled={submitting}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                padding: '10px 24px', borderRadius: 'var(--radius-sm)',
                border: 'none', background: 'var(--success)', color: '#fff',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? 'Activating...' : 'Activate Workspace'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
